import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { devError } from '../lib/devLog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Editorial band that promotes Sebastian Magnani's Daily Bat Volume III —
 * the seven 2025 pieces. Renders a mini gallery of thumbnails, each
 * linking straight to that artwork's PDP; the header + CTA jump to the
 * Vol III band on Magnani's artist page (`#magnani-vol-iii` anchor).
 *
 * Hidden gracefully if the API returns no Vol III works.
 */
const MagnaniVolIIITrailer = () => {
  const [works, setWorks] = useState([]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/artworks?artist=${encodeURIComponent('Sebastian Magnani')}&limit=200`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        const volIII = (data || [])
          .filter((w) => (w.series || '') === 'Daily Bat – Volume III' && w.imageUrl)
          .sort((a, b) => (a.title || '').localeCompare(b.title || ''));
        setWorks(volIII);
      })
      .catch((err) => devError('Vol III trailer fetch failed:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  if (works.length === 0) return null;

  return (
    <section
      className="relative bg-neutral-950 text-white overflow-hidden"
      data-testid="magnani-vol-iii-trailer"
    >
      {/* Editorial header + CTA */}
      <div className="max-w-[1400px] mx-auto px-6 pt-20 lg:pt-24 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <p className="text-[10px] uppercase tracking-[0.4em] text-neutral-500 mb-4">
              New for 2025 · Sebastian Magnani
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-6">
              Daily Bat, Volume III
            </h2>
            <p className="max-w-2xl text-base md:text-lg text-neutral-300 leading-relaxed">
              Seven new photographs from Magnani&rsquo;s ongoing series, made this year in Zermatt,
              Sydney, Martigny and the Coca-Cola-lit corners of London. Batman moves through
              the everyday — a hotel library, a lighthouse cliff, a candy-pink donut shop —
              carrying, as ever, a shared sense of quiet.
            </p>
          </div>
          <div className="lg:col-span-4 lg:justify-self-end">
            <Link
              to="/artist/sebastian-magnani#magnani-vol-iii"
              className="group inline-flex items-center gap-3 px-6 py-3.5 border border-white/40 text-xs uppercase tracking-[0.2em] font-medium hover:bg-white hover:text-neutral-950 transition-colors"
              data-testid="magnani-vol-iii-see-all"
            >
              See all {works.length} pieces
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </div>

      {/* Mini gallery: 4-col grid on desktop, horizontal scroll on mobile */}
      <div className="pb-24">
        <div
          className="flex gap-4 lg:grid lg:grid-cols-4 lg:gap-6 overflow-x-auto snap-x snap-mandatory scrollbar-hide px-6 lg:max-w-[1400px] lg:mx-auto"
          style={{ scrollbarWidth: 'none' }}
          data-testid="magnani-vol-iii-track"
        >
          {works.map((w, i) => {
            const src = w.imageUrl.startsWith('http') ? w.imageUrl : `${API_URL}${w.imageUrl}`;
            return (
              <motion.div
                key={w.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: Math.min(i, 6) * 0.06, ease: [0.22, 1, 0.36, 1] }}
                className="shrink-0 w-[75%] sm:w-[45%] md:w-[32%] lg:w-auto snap-start"
              >
                <Link
                  to={`/artwork/${w.slug || w.id}`}
                  className="group block"
                  data-testid={`magnani-vol-iii-tile-${w.slug || w.id}`}
                >
                  <div className="relative aspect-[3/4] overflow-hidden bg-neutral-900 mb-4">
                    <img
                      src={src}
                      alt={`${w.title} — Sebastian Magnani, Daily Bat Volume III`}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                    />
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-1">
                    {String(i + 1).padStart(2, '0')} / {String(works.length).padStart(2, '0')} · 2025
                  </p>
                  <h3 className="font-serif text-xl leading-snug mb-1 group-hover:text-white/80 transition-colors">
                    {w.title}
                  </h3>
                  <p className="text-xs text-neutral-400">
                    From {w.priceDisplay || 'Price on Request'}
                  </p>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default MagnaniVolIIITrailer;
